import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

/**
 * App-wide button primitive. Eight variants for hierarchy + tone, three
 * sizes for density. The 44px touch target is preserved via `min-h-touch`
 * so reception and station staff using tablets always have a comfortable
 * tap surface.
 *
 * Aurora's visual identity lands here:
 *   - primary  → indigo→pink gradient with soft accent shadow
 *   - success  → teal→cyan gradient (signature for "complete" / "issued")
 *   - warning  → amber surface (reversible, attention-worthy)
 *   - destructive → rose surface (irreversible)
 *   - info     → soft sky accent (neutral-positive)
 *   - secondary → token-driven outline
 *   - subtle   → soft surface
 *   - ghost    → hover-only background
 *
 * All variants react to theme switches because they consume CSS variable
 * colours via the semantic Tailwind layer (bg-accent, text-fg, ...).
 */

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'destructive'
  | 'ghost'
  | 'subtle'
  | 'success'
  | 'info'
  | 'warning';

export type ButtonSize = 'sm' | 'md' | 'lg';

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  // Indigo→pink gradient — Aurora's signature CTA. Shadow uses the
  // accent token so dark mode gets a violet glow.
  primary:
    'text-fg-on-accent shadow-token-accent ' +
    '[background-image:linear-gradient(135deg,rgb(var(--accent))_0%,#ec4899_100%)] ' +
    'hover:brightness-110 active:brightness-95 disabled:opacity-50 disabled:shadow-none',
  secondary:
    'border border-line-strong/20 bg-surface text-fg shadow-token-sm ' +
    'hover:bg-surface-2 active:bg-surface-inset disabled:opacity-50',
  destructive:
    'bg-danger text-danger-fg shadow-token-sm ' +
    'hover:brightness-110 active:brightness-95 disabled:opacity-50',
  ghost:
    'text-fg-muted hover:bg-surface-2 active:bg-surface-inset disabled:opacity-50',
  subtle:
    'bg-surface-2 text-fg hover:bg-surface-inset active:brightness-95 disabled:opacity-50',
  // Teal→cyan gradient for "complete" / "good path" actions.
  success:
    'text-success-fg shadow-token-sm ' +
    '[background-image:linear-gradient(135deg,rgb(var(--success))_0%,#06b6d4_100%)] ' +
    'hover:brightness-110 active:brightness-95 disabled:opacity-50 disabled:shadow-none',
  info:
    'bg-info/10 text-info hover:bg-info/15 active:bg-info/20 disabled:opacity-50',
  warning:
    'bg-warning text-warning-fg shadow-token-sm ' +
    'hover:brightness-105 active:brightness-95 disabled:opacity-50',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  // 'sm' for inline table actions; still keeps a comfortable touch
  // target on tablet via the min-h.
  sm: 'h-8 min-h-touch px-3 text-xs sm:min-h-0 sm:h-7',
  md: 'h-10 min-h-touch px-4 text-sm',
  lg: 'h-12 min-h-touch px-5 text-base',
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Optional icon component (lucide-react). Rendered before children. */
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  /** Show a spinner and disable the button while a request is in flight. */
  loading?: boolean;
  fullWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = 'primary',
      size = 'md',
      iconLeft,
      iconRight,
      loading = false,
      fullWidth = false,
      className = '',
      disabled,
      children,
      type = 'button',
      ...rest
    },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled || loading}
        className={[
          'inline-flex items-center justify-center gap-2 rounded-token font-medium',
          'transition-all duration-150',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-strong focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
          'disabled:cursor-not-allowed',
          fullWidth ? 'w-full' : '',
          VARIANT_CLASSES[variant],
          SIZE_CLASSES[size],
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        {...rest}
      >
        {loading ? (
          <Spinner />
        ) : iconLeft ? (
          <span className="shrink-0" aria-hidden>
            {iconLeft}
          </span>
        ) : null}
        {children}
        {!loading && iconRight ? (
          <span className="shrink-0" aria-hidden>
            {iconRight}
          </span>
        ) : null}
      </button>
    );
  },
);

function Spinner() {
  return (
    <svg
      className="h-4 w-4 animate-spin"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
    >
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeOpacity="0.25"
        strokeWidth="3"
      />
      <path
        d="M22 12a10 10 0 0 1-10 10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}
